import DragHandle from '@tiptap/extension-drag-handle-react';
import FindAndReplace from '@tiptap/extension-find-and-replace';
import HardBreak from '@tiptap/extension-hard-break';
import Highlight from '@tiptap/extension-highlight';
import { ListItem, TaskItem, TaskList } from '@tiptap/extension-list';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import { Table, TableCell, TableHeader, TableRow } from '@tiptap/extension-table';
import Typography from '@tiptap/extension-typography';
import { CharacterCount, Placeholder } from '@tiptap/extensions';
import { Markdown } from '@tiptap/markdown';
import { Node, Slice } from '@tiptap/pm/model';
import { EditorView } from '@tiptap/pm/view';
import { Content, Editor, EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import {
  FunctionComponent,
  memo,
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { isMobileOnly } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { StoryCardPostContentBlockFields } from '../../../services';
import { FileAcceptType } from '../../hooks';
import { htmlToMarkdown, optArrItem } from '../../utils';
import { IconLabel } from '../IconLabel';
import { BlockMover } from './BlockMover';
import { BlockTools, TableMenu } from './BlockTools';
import { FindReplaceBar } from './FindReplaceBar';
import { fixColumnWidthsOnResize } from './fixColumnWidthsOnResize';
import { InlineTools } from './InlineTools';
import {
  AudioNode,
  CalloutNode,
  CodeNode,
  DelimiterNode,
  EmbedLinkNode,
  FileNode,
  ImageNode,
  PdfNode,
  QuoteNode,
  StoryCardNode,
  StoryNode,
  UserNode,
  VideoNode,
} from './Nodes';

import classNames from 'classnames';
import classes from './TextEditor.module.scss';

const MARKDOWN_PATTERNS = [
  /^#{1,6} /m,
  /^ *[-*+] /m,
  /^ *\d+[.)] .*\r?\n *\d+[.)] /m,
  /^>/m,
  /^(```|~~~)/m,
  /^\|.*\|$/m,
  /^[-*_]{3,}$/m,
  /\[[^\]]+\]\([^)]+\)/,
];

export enum TextEditorVariant {
  FULL_POST = 'FULL_POST',
  SHORT_POST = 'SHORT_POST',
  EVENT = 'EVENT',
}

interface TextEditorProps {
  variant: TextEditorVariant;
  content: Content;
  editorRef?: MutableRefObject<Editor | null>;
  onUpdateHandler?: (content: string | StoryCardPostContentBlockFields[]) => void;
  onPasteHandler?: (text: string) => void;
  onEditorChange?: (editor: Editor | null) => void;
  placeholder?: string;
  locale?: string;
  className?: string;
}

interface DropPasteMediaHandlerArgs {
  view: EditorView;
  event: Event;
  files: File[];
}

export const FULL_POST_EDITOR_DEFAULT_CONTENT = '<h1></h1><p></p>';

export const TABLE_CELL_MIN_WIDTH = 100;

const isValidNode = (node: Node): boolean =>
  node.type.validContent(node.content) && node.children.every(isValidNode);

const dragStartHandler = ({ dataTransfer }: DragEvent) => {
  if (!dataTransfer) {
    return;
  }

  dataTransfer.effectAllowed = 'copyMove';
};

export const TextEditor: FunctionComponent<TextEditorProps> = memo(
  ({
    variant,
    content,
    onUpdateHandler,
    editorRef,
    onPasteHandler,
    onEditorChange,
    placeholder,
    locale,
    className,
  }) => {
    const { t } = useTranslation();

    const { t: ctxT } = useTranslation('', { keyPrefix: 'richTextEditor' });

    const [isReady, setIsReady] = useState(false);

    const hoveredBlockRef = useRef<HTMLDivElement>(null);

    const isCompact = useMemo(() => variant !== TextEditorVariant.FULL_POST, [variant]);

    const isMobileFullPost = !isCompact && isMobileOnly;

    const editorLabel = isCompact ? placeholder : t('common.long-post');

    const placeholderSetup = useCallback(
      ({ editor, node }: { editor: Editor; node: Node }) => {
        if (!isCompact) {
          if (node.type.name === 'paragraph') {
            return ctxT('paragraph-config-placeholder');
          }

          if (node.type.name === 'heading') {
            return ctxT('header-config-placeholder');
          }

          return '';
        }

        if (
          editor.state.doc.content.content.length === 1 &&
          editor.isEmpty &&
          node.type.name === 'paragraph'
        ) {
          return placeholder ?? '';
        }

        return '';
      },
      [ctxT, isCompact, placeholder]
    );

    const dropPasteNode = useCallback(({ state }: EditorView, file: File) => {
      const { schema } = state;

      const { type, name } = file;

      const extension = name.slice(name.lastIndexOf('.')).toLowerCase();

      const docExtensions = FileAcceptType.FILE.split(',');

      const baseAttrs = { items: [], pastedFiles: [file] };

      if (type.startsWith('image/')) {
        return schema.nodes.imageNode.create({
          ...baseAttrs,
          withBorder: false,
          withBackground: false,
          stretched: false,
        });
      } else if (type.startsWith('audio/')) {
        return schema.nodes.audioNode.create(baseAttrs);
      } else if (type.startsWith('video/')) {
        return schema.nodes.videoNode.create(baseAttrs);
      } else if (type === 'application/pdf') {
        return schema.nodes.pdfNode.create(baseAttrs);
      } else if (docExtensions.includes(extension)) {
        return schema.nodes.fileNode.create(baseAttrs);
      }

      return null;
    }, []);

    const dropPasteMediaHandler = useCallback(
      ({ view, event, files }: DropPasteMediaHandlerArgs) => {
        if (!files.length) {
          return false;
        }

        const { state } = view;

        const pos =
          event instanceof DragEvent
            ? view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos
            : state.selection.from;

        if (pos == null) {
          return false;
        }

        const { schema, tr } = view.state;

        const isGallery = files.every((file) => file.type.startsWith('image/'));

        if (isGallery) {
          view.dispatch(
            tr.insert(
              pos,
              schema.nodes.imageNode.create({
                items: [],
                pastedFiles: files,
                withBorder: false,
                withBackground: false,
                stretched: false,
              })
            )
          );
          return true;
        }

        let transaction = tr;

        let insertPosition = pos;

        for (const file of files) {
          const node = dropPasteNode(view, file);

          if (node) {
            transaction = transaction.insert(insertPosition, node);
            insertPosition += node.nodeSize;
          }
        }

        view.dispatch(transaction);

        return true;
      },
      [dropPasteNode]
    );

    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          ...(isCompact && { heading: false, bulletList: false, orderedList: false }),
          ...(!isCompact && {
            blockquote: false,
            codeBlock: false,
            horizontalRule: false,
            listItem: false,
            dropcursor: {
              color: 'var(--icon-label-with-active-text-color)',
              width: 2,
              class: classes['text-editor__drop-line'],
            },
          }),
          link: { openOnClick: false },
          trailingNode: { node: 'paragraph' },
          hardBreak: false,
        }),
        HardBreak.extend({
          addKeyboardShortcuts() {
            return {};
          },
        }),
        Placeholder.configure({
          placeholder: placeholderSetup,
          showOnlyCurrent: isCompact,
          showOnlyWhenEditable: true,
        }),
        CharacterCount.configure({
          wordCounter: (text) => text.split(/\s+/).filter(Boolean).length,
        }),
        FindAndReplace,
        Highlight,
        Subscript,
        Superscript,
        ...optArrItem(
          !isCompact,
          ListItem.extend({ content: 'paragraph (bulletList | orderedList)?' })
        ),
        TaskList,
        TaskItem.extend({ ...(!isCompact && { content: 'paragraph' }) }).configure({
          nested: false,
        }),
        Table.extend({
          addProseMirrorPlugins() {
            return [fixColumnWidthsOnResize(), ...(this.parent?.() ?? [])];
          },
        }).configure({ resizable: !isCompact, cellMinWidth: TABLE_CELL_MIN_WIDTH }),
        TableRow,
        TableHeader.extend({ ...(!isCompact && { content: 'paragraph+' }) }),
        TableCell.extend({ ...(!isCompact && { content: 'paragraph+' }) }),
        ...optArrItem(
          !isCompact,
          Typography.configure({
            emDash: '–',
            ...(locale === 'de-DE' && {
              openDoubleQuote: '„',
              closeDoubleQuote: '“',
              openSingleQuote: '‚',
            }),
          })
        ),
        ...optArrItem(!isCompact, Markdown.configure({ markedOptions: { breaks: true } })),
        //custom nodes
        DelimiterNode,
        EmbedLinkNode,
        QuoteNode,
        CodeNode,
        CalloutNode,
        UserNode,
        StoryNode,
        StoryCardNode,
        ImageNode,
        AudioNode,
        VideoNode,
        PdfNode,
        FileNode,
      ],
      content,
      editorProps: {
        ...(editorLabel && { attributes: { 'aria-label': editorLabel } }),
        handleDrop(view, event) {
          if (isCompact) {
            return false;
          }

          const hasFiles = event.dataTransfer?.files?.length;

          if (!hasFiles) {
            return false;
          }

          const posAtCoords = editor.view.posAtCoords({ left: event.clientX, top: event.clientY });

          if (!posAtCoords) {
            return;
          }

          if (editor.state.doc.resolve(posAtCoords.pos).depth > 1) {
            return true;
          }

          dropPasteMediaHandler({ view, event, files: Array.from(event.dataTransfer.files) });
        },
        handlePaste(view, event) {
          const text = event.clipboardData?.getData('text/plain') ?? '';

          onPasteHandler?.(text);

          if (isCompact) {
            return false;
          }

          const { state } = view;

          if (state.doc.resolve(state.selection.from).depth > 1) {
            return false;
          }

          const files = Array.from(event.clipboardData?.items || [])
            .map((item) => item.getAsFile())
            .filter((file) => Boolean(file)) as File[];

          const { markdown } = editor;

          if (
            markdown &&
            !files.length &&
            !event.clipboardData?.types.includes('text/html') &&
            MARKDOWN_PATTERNS.some((pattern) => pattern.test(text))
          ) {
            const markdownDoc = state.schema.nodeFromJSON(markdown.parse(text));

            if (!isValidNode(markdownDoc)) {
              return false;
            }

            view.dispatch(
              state.tr
                .replaceSelection(Slice.maxOpen(markdownDoc.content))
                .scrollIntoView()
                .setMeta('paste', true)
                .setMeta('uiEvent', 'paste')
            );

            return true;
          }

          dropPasteMediaHandler({ view, event, files });
        },
      },
      onCreate() {
        setIsReady(true);
      },
      onUpdate({ editor }) {
        if (!onUpdateHandler) {
          return;
        }

        onUpdateHandler(
          htmlToMarkdown(
            editor
              .getHTML()
              .replace(/<p>(.*?)<\/p>/g, '$1\n')
              .trim() ?? ''
          )
        );
      },
    });

    const nodeChangeHandler = useCallback(
      ({ node, pos }: { node: Node | null; pos: number }) => {
        const { current: hoveredBlock } = hoveredBlockRef;
        const blockElement = node && editor.view.nodeDOM(pos);

        if (!hoveredBlock || !(blockElement instanceof HTMLElement)) {
          return;
        }

        hoveredBlock.style.width = `${blockElement.offsetWidth}px`;
        hoveredBlock.style.height = `${blockElement.offsetHeight}px`;
      },
      [editor]
    );

    const dragHandleLeaveHandler = useCallback(
      ({ relatedTarget }: React.MouseEvent<HTMLDivElement>) => {
        if (relatedTarget instanceof Element && editor.view.dom.contains(relatedTarget)) {
          return;
        }

        editor.commands.setMeta('hideDragHandle', true);
      },
      [editor]
    );

    useEffect(() => {
      if (!editor || !editorRef) {
        return;
      }

      editorRef.current = editor;

      return () => {
        editorRef.current = null;
      };
    }, [editor, editorRef]);

    useEffect(() => {
      if (!onEditorChange) {
        return;
      }

      onEditorChange(editor);

      return () => onEditorChange(null);
    }, [editor, onEditorChange]);

    useEffect(() => {
      if (!isReady || !editor) {
        return;
      }

      if (isCompact || editor.getHTML() === FULL_POST_EDITOR_DEFAULT_CONTENT) {
        setTimeout(() => editor.commands.focus('end'), 0);
      }
    }, [editor, isReady, isCompact]);

    if (!editor || !editor.view || !isReady) {
      return null;
    }

    return (
      <div
        className={classNames(
          classes['text-editor'],
          {
            [classes['text-editor--full-post']]: !isCompact,
            [classes['text-editor--mobile']]: isMobileFullPost,
          },
          className
        )}
      >
        {!isCompact && (
          <>
            <BlockTools editor={editor} />
            <FindReplaceBar editor={editor} />
            {!isMobileOnly && (
              <DragHandle
                editor={editor}
                onNodeChange={nodeChangeHandler}
                onElementDragStart={dragStartHandler}
              >
                <div onMouseLeave={dragHandleLeaveHandler}>
                  <IconLabel
                    iconId={'drag'}
                    iconSize={16}
                    className={classes['text-editor__drag-handle']}
                  />
                </div>
                <div ref={hoveredBlockRef} className={classes['text-editor__hovered-block']} />
              </DragHandle>
            )}
          </>
        )}

        <EditorContent editor={editor} className={classNames({ scrollable: !isCompact })} />

        {isMobileFullPost && <BlockMover editor={editor} />}

        <InlineTools editor={editor} isCompact={isCompact} />

        <TableMenu editor={editor} />
      </div>
    );
  }
);
